import ApiService from '@/data/services/ApiService'
import { IQuestionnaire } from '@/data/models/Questionnaire'
import { AxiosResponse } from 'axios'
import { IServiceResponse } from '@/data/models/ServiceResponse'

export class QuestionnaireApiService extends ApiService {
  /**
   * Returns list of Questionnaires for the organization
   */
  static async index(params: Record<string, any> = {}): Promise<IServiceResponse<IQuestionnaire[]>> {
    return this.axios.get('questionnaires', { params })
      .then((response: AxiosResponse<IServiceResponse<IQuestionnaire[]>>) => response.data)
  }

  /**
   * Given questionnaire's id or type, fetches questionnaire data from database
   *
   * @param idOrType - The ID or type of the questionnaire to find
   */
  static async find(idOrType: string | number): Promise<IServiceResponse<IQuestionnaire>> {
    return this.axios.get(`questionnaires/${idOrType}`)
      .then((response: AxiosResponse<IServiceResponse<IQuestionnaire>>) => response.data)
  }

  /**
   * Creates a new questionnaire
   *
   * @param data - The questionnaire data to create
   */
  static async create(data: object): Promise<IServiceResponse<IQuestionnaire>> {
    return this.axios.post('questionnaires', data)
      .then((response: AxiosResponse<IServiceResponse<IQuestionnaire>>) => response.data)
  }

  /**
   * Updates an existing questionnaire
   *
   * @param questionnaireId - ID of the questionnaire to update
   * @param data - The updated questionnaire data
   */
  static async update(questionnaireId: string | number, data: object): Promise<IServiceResponse<IQuestionnaire>> {
    return this.axios.put(`questionnaires/${questionnaireId}`, data)
      .then((response: AxiosResponse<IServiceResponse<IQuestionnaire>>) => response.data)
  }

  /**
   * Deletes a questionnaire
   *
   * @param questionnaireId - ID of the questionnaire to delete
   */
  static async delete(questionnaireId: string | number): Promise<AxiosResponse> {
    return this.axios.delete(`questionnaires/${questionnaireId}`)
  }

  static async getInstances(params: Record<string, any> = {}): Promise<IServiceResponse<any>> {
    return this.axios.get('questionnaire-instances', { params })
      .then((response: AxiosResponse<IServiceResponse<any>>) => response.data)
  }

  static async findInstance(id: string | number): Promise<IServiceResponse<any>> {
    return this.axios.get(`questionnaire-instances/${id}`)
      .then((response: AxiosResponse<IServiceResponse<any>>) => response.data)
  }

  static async createInstance(data: object): Promise<IServiceResponse<object>> {
    return this.axios.post('questionnaire-instances', data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async updateInstance(questionnaireInstanceId: string | number, data: object): Promise<IServiceResponse<object>> {
    return this.axios.put(`questionnaire-instances/${questionnaireInstanceId}`, data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async deleteInstance(questionnaireInstanceId: string | number): Promise<AxiosResponse> {
    return this.axios.delete(`questionnaire-instances/${questionnaireInstanceId}`)
  }

  static async storeFeedback(questionnareInstanceId: string | number, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`questionnaire-instances/${questionnareInstanceId}/feedback`, data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getStats(data: object): Promise<IServiceResponse<object>> {
    return this.axios.get('questionnaires/stats', { params: data })
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }
}
